import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Validators } from '@angular/forms';
import { CurrencyService } from '@core/services/currency.service';
import { APIAdminClient } from '@core/typings/api/admin-client.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { InKindService } from '@features/in-kind/in-kind.service';
import { InKindCategories } from '@features/in-kind/in-kind.typing';
import { SupportService } from '@features/support/support.service';
import { FormControlIcon, TypeSafeFormBuilder, TypeSafeFormGroup } from '@yourcause/common/core-forms';
import { TrackingEventNames } from '@yourcause/common/heap';
import { Subscription } from 'rxjs';
import { CreateEditBudgetScenario } from '../create-edit-budget-modal/create-edit-budget-modal.component';
export interface AddInKindBudgetGroup {
  itemName: string;
  units: number;
  value: number;
  identification: string;
  categoryId: number;
  allocatedUnits: number;
}
export interface InKindCategoryGroup {
  name: string;
  type: InKindCategories;
  defaultLang: string;
}

@Component({
    selector: 'gc-budget-add-in-kind-item',
    templateUrl: './budget-add-in-kind-item.component.html',
    styleUrls: ['./budget-add-in-kind-item.component.scss'],
    standalone: false
})
export class BudgetAddInKindItemComponent implements OnInit, OnChanges, OnDestroy {
  @Input() scenario: CreateEditBudgetScenario;
  @Input() newCategoryId: number;
  @Output() onAddingCategory = new EventEmitter<boolean>();
  @Output() onValidChange = new EventEmitter<boolean>();
  @Output() onChange = new EventEmitter<AddInKindBudgetGroup>();
  @Output() onCategoryChange = new EventEmitter<InKindCategoryGroup>();

  sub = new Subscription();
  formGroup: TypeSafeFormGroup<AddInKindBudgetGroup>;
  categoryGroup: TypeSafeFormGroup<InKindCategoryGroup>;
  categoryOptions = this.inKindService.categoryOptions;
  typeOptions = this.inKindService.getTypeOptions();
  addingCategory = false;
  currencyData = this.currencyService.formattingData[
    this.clientSettingsService.defaultCurrency
  ];
  rightIcons: FormControlIcon[] = [{
    icon: this.clientSettingsService.defaultCurrency,
    isStaticText: true
  }];
  langKeys = this.clientSettingsService.selectedLanguages;
  hasInternational = this.clientSettingsService.doesClientHaveClientFeature(
    APIAdminClient.ClientFeatureTypes.HasInternational
  );
  uniqueIdHelpLink = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-in-kind-add-items.html';
  inKindTypeHelpLink = 'https://webfiles.blackbaud.com/files/support/helpfiles/grantsconnect/content/gc-in-kind-create-new-category.html';
  isAfterInit = false;

  constructor (
    private formBuilder: TypeSafeFormBuilder,
    private inKindService: InKindService,
    private currencyService: CurrencyService,
    private clientSettingsService: ClientSettingsService,
    private supportService: SupportService
  ) { }

  ngOnInit () {
    this.formGroup = this.formBuilder.group<AddInKindBudgetGroup>({
      itemName: ['', Validators.required],
      units: null,
      value: [null, Validators.required],
      identification: ['', Validators.required],
      categoryId: [null, Validators.required],
      allocatedUnits: null
    });
    this.categoryGroup = this.formBuilder.group<InKindCategoryGroup>({
      name: ['', Validators.required],
      type: [null, Validators.required],
      defaultLang: [this.clientSettingsService.defaultLanguage, Validators.required]
    });
    this.sub.add(this.formGroup.valueChanges.subscribe(() => {
      this.onChange.emit(this.formGroup.value);
      if (!this.addingCategory) {
        this.onValidChange.emit(this.formGroup.valid);
      }
    }));
    this.sub.add(this.categoryGroup.valueChanges.subscribe(() => {
      this.onCategoryChange.emit(this.categoryGroup.value);
      if (this.addingCategory) {
        this.onValidChange.emit(this.categoryGroup.valid);
      }
    }));
    this.onValidChange.emit(this.formGroup.valid);
    this.isAfterInit = true;
  }

  ngOnChanges (changes: SimpleChanges) {
    if (this.isAfterInit) {
      if (changes.scenario) {
        if (
          this.scenario === CreateEditBudgetScenario.AddInKindCategory &&
          !this.addingCategory
        ) {
          this.addingCategory = true;
          this.onValidChange.emit(this.categoryGroup.valid);
        } else if (
          this.scenario === CreateEditBudgetScenario.AddInKindItem &&
          this.addingCategory
        ) {
          this.addingCategory = false;
          this.onValidChange.emit(this.formGroup.valid);
        }
      }
      if (changes.newCategoryId && this.newCategoryId) {
        this.categoryOptions = this.inKindService.categoryOptions;
        this.formGroup.get('categoryId').setValue(this.newCategoryId);
      }
    }
  }

  onNewCategory () {
    this.addingCategory = true;
    this.onAddingCategory.emit(true);
    this.onValidChange.emit(this.categoryGroup.valid);
  }

  trackClick (link: string) {
    this.supportService.trackClick(TrackingEventNames.HelpLink, link)
  }
  
  ngOnDestroy () {
    this.sub.unsubscribe();
  }
}
